import * as THREE from 'three';
import { surfaceHeight } from '@/world/chunk';
import { farColour, farHeight, type FarBounds, type FarStores } from '@/world/farField';
import type { Region } from '@/world/region';
import { dryness, DRY_GLSL } from '@/render/season';

export interface Horizon {
  mesh: THREE.Mesh;
  /** Cells per side of the occupancy window (must match the caller's bitmap). */
  readonly holeCells: number;
  /**
   * Cut the hole per cell: `occupancy` is holeCells² bytes, row-major from
   * minimum z, 255 where a chunk is mounted. `originX/Z` is the world corner
   * of the window's first cell, `cellSize` the chunk size in metres.
   */
  setHole(originX: number, originZ: number, cellSize: number, occupancy: Uint8Array): void;
  /**
   * Re-read the world's sparse history into the far mesh — heights from
   * `earth`, colour from `env` and `overlay`. Pass a world-space box to redo
   * only the vertices inside it; omit it to redo everything.
   *
   * Cheap enough to call whenever a store's version moves and far too
   * expensive to call every frame: at 320² a full pass is ~102k vertices,
   * each costing a height sample and four colour taps.
   */
  refresh(now: number, bounds?: FarBounds | null): void;
}

/** ±24 cells around the focus — the widest ring the streamer ever wants. */
const HOLE_CELLS = 49;

/**
 * One coarse mesh covering the whole region out to the horizon. Chunks refine
 * the ground near the focus; this carries the rest of the world, so pulling
 * the camera out reveals an island — not a rug of chunks ending in fog.
 *
 * It is coarse: it has none of the chunks' detail relief, so it must never be
 * visible where chunks are. A moving hole is discarded in the fragment shader
 * under the streamed ring — the horizon exists only beyond it.
 *
 * Built once from the immutable Region, at detail 0, with the same colour
 * function the chunks use — so the far ground fades into the near, not off it.
 */
export function buildHorizon(
  region: Region, res = 320,
  /**
   * The sparse stores the far view should read. Omitted in tests and probes,
   * where the horizon is the bare baseline it always was.
   */
  stores: FarStores = {},
): Horizon {
  const ext = region.extentMetres;
  const step = ext / (res - 1);
  const half = ext / 2;

  const geo = new THREE.PlaneGeometry(ext, ext, res - 1, res - 1);
  geo.rotateX(-Math.PI / 2);
  const pos = geo.attributes.position as THREE.BufferAttribute;

  const height = new Float32Array(res * res);
  const colour = new Float32Array(res * res * 3);
  // colours are supersampled: a vertex every ~200 m aliases features narrower
  // than its cell — the beach ribbon, the shore shelf — into whole pale
  // triangles. Four spread taps per vertex turn those into gradients.
  const q = step * 0.27;
  const TAPS = [[-q, -q], [q, -q], [-q, q], [q, q]] as const;
  const wx = (i: number) => -half + i * step;
  const wz = (j: number) => -half + j * step;

  /** Heights for a range of rows. Must run before colours — slope reads it. */
  const fillHeights = (i0: number, i1: number, j0: number, j1: number) => {
    for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) {
      height[j * res + i] = stores.earth
        ? farHeight(region, stores, wx(i), wz(j))
        : surfaceHeight(region, wx(i), wz(j), 0);
    }
  };

  const fillColours = (i0: number, i1: number, j0: number, j1: number, now: number) => {
    for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) {
      const k = j * res + i;
      const l = height[j * res + Math.max(0, i - 1)], r = height[j * res + Math.min(res - 1, i + 1)];
      const u = height[Math.max(0, j - 1) * res + i], d = height[Math.min(res - 1, j + 1) * res + i];
      const slope = Math.hypot((r - l) / (2 * step), (d - u) / (2 * step));
      let cr = 0, cg = 0, cb = 0;
      for (const [ox, oz] of TAPS) {
        const c = farColour(region, stores, wx(i) + ox, wz(j) + oz, slope, now);
        cr += c[0]; cg += c[1]; cb += c[2];
      }
      colour[k * 3] = cr / 4; colour[k * 3 + 1] = cg / 4; colour[k * 3 + 2] = cb / 4;
    }
  };

  fillHeights(0, res - 1, 0, res - 1);
  fillColours(0, res - 1, 0, res - 1, 0);
  for (let k = 0; k < pos.count; k++) pos.setY(k, height[k]);
  geo.setAttribute('color', new THREE.BufferAttribute(colour, 3));
  geo.computeVertexNormals();
  geo.computeBoundingSphere();

  // per-cell occupancy: the hole is exactly the mounted cells, nothing more.
  // A square hole sized to the intended ring either shows the void while
  // chunks stream, or (sized to what is covered) lets this coarse relief
  // poke up between mounted chunks beyond it. Per cell, both vanish.
  const holeData = new Uint8Array(HOLE_CELLS * HOLE_CELLS);
  const holeTex = new THREE.DataTexture(holeData, HOLE_CELLS, HOLE_CELLS, THREE.RedFormat);
  holeTex.magFilter = holeTex.minFilter = THREE.NearestFilter;
  holeTex.wrapS = holeTex.wrapT = THREE.ClampToEdgeWrapping;
  const uHoleWin = { value: new THREE.Vector3(0, 0, 0) };   // origin x, z, 1/span
  const mat = new THREE.MeshStandardMaterial({
    vertexColors: true, roughness: 0.98, metalness: 0,
  });
  mat.onBeforeCompile = (sh) => {
    sh.uniforms.uHoleTex = { value: holeTex };
    sh.uniforms.uHoleWin = uHoleWin;
    sh.uniforms.uDry = dryness;
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vHzW;')
      .replace('#include <begin_vertex>',
        '#include <begin_vertex>\nvHzW = (modelMatrix * vec4(position, 1.0)).xyz;');
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>',
        '#include <common>\nvarying vec3 vHzW;\nuniform sampler2D uHoleTex;\nuniform vec3 uHoleWin;'
        + DRY_GLSL)
      // The season, on the far ground too — or the island cures out to the
      // streamed ring and stays spring-green beyond it, which is a seam the
      // eye finds immediately.
      //
      // The mask cannot be the near field's: this mesh has no `aMat`, because
      // it is 320² vertices refilled on the CPU and carries a baked colour and
      // nothing else. What it DOES carry is `vHzW`, the world position with
      // its height in it, so the cure is gated on elevation instead — nothing
      // at or under the sea, full through the ground people live on, fading
      // out above the treeline where the land is rock and cures no further.
      // It is an approximation of the near mask and is not claimed to match it
      // vertex for vertex; at horizon range the aerial perspective is doing
      // far more to the colour than this is, and the alternative is a green
      // far ground behind a straw near one.
      .replace('#include <color_fragment>', `#include <color_fragment>
        {
          float land = smoothstep(2.0, 40.0, vHzW.y);
          float alp  = 1.0 - smoothstep(700.0, 1500.0, vHzW.y);
          diffuseColor.rgb = seasonTint(diffuseColor.rgb, land * alp * 0.62, uDry);
        }`)
      .replace('#include <clipping_planes_fragment>', `
        {
          vec2 huv = (vHzW.xz - uHoleWin.xy) * uHoleWin.z;
          if (huv.x > 0.0 && huv.x < 1.0 && huv.y > 0.0 && huv.y < 1.0
              && texture2D(uHoleTex, huv).r > 0.5) discard;
        }
        #include <clipping_planes_fragment>`);
  };
  const mesh = new THREE.Mesh(geo, mat);
  mesh.receiveShadow = true;
  mesh.position.y = -1.5;   // strictly beneath the chunk skirt line — no z-fighting
  return {
    mesh,
    holeCells: HOLE_CELLS,
    setHole(originX, originZ, cellSize, occupancy) {
      holeData.set(occupancy);
      holeTex.needsUpdate = true;
      uHoleWin.value.set(originX, originZ, 1 / (HOLE_CELLS * cellSize));
    },
    refresh(now, bounds) {
      // one cell of margin: a vertex's colour reads its neighbours' heights,
      // so the ring just outside a changed box has to be redone too
      const idx = (w: number) => Math.round((w + half) / step);
      const i0 = bounds ? Math.max(0, idx(bounds.x0) - 2) : 0;
      const i1 = bounds ? Math.min(res - 1, idx(bounds.x1) + 2) : res - 1;
      const j0 = bounds ? Math.max(0, idx(bounds.z0) - 2) : 0;
      const j1 = bounds ? Math.min(res - 1, idx(bounds.z1) + 2) : res - 1;
      if (i1 < i0 || j1 < j0) return;

      fillHeights(i0, i1, j0, j1);
      fillColours(i0, i1, j0, j1, now);
      for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) {
        pos.setY(j * res + i, height[j * res + i]);
      }
      pos.needsUpdate = true;
      (geo.attributes.color as THREE.BufferAttribute).needsUpdate = true;
      // normals are cheap enough at this resolution and wrong if left stale:
      // a raised hill with the old normals is a flat patch of odd shading
      geo.computeVertexNormals();
      geo.computeBoundingSphere();
    },
  };
}
